// Minimal mob sprite renderer for in-expedition mobs.
//
// Step 15b: spawn sprite on mobSpawned / destroy on mobDefeated. Step
// 16a: per-frame `sync(mobPositions)` drives world-space position from
// combat-state.mobPositions (refreshed by the expeditionPositions
// ephemeral broadcast). The sprite-lifecycle events remain the
// authoritative add/remove; sync only moves existing sprites.
//
// Click handler emits a target-set callback so combat-state's targetId
// flips without the renderer having to import combat-state. Keeps the
// rendering layer free of reducer dependencies.

import { Container, Graphics } from "pixi.js";

const MOB_SIZE_PX = 20; // contracts:allow-literal — mob sprite side length
const MOB_COLOR = 0xcc3333;

export type MobRendererDeps = {
  worldContainer: Container;
  onTargetClicked: (mobId: string) => void;
};

export class MobRenderer {
  private readonly sprites = new Map<string, Graphics>();

  constructor(private readonly deps: MobRendererDeps) {}

  spawn(mobId: string, x: number, y: number): void {
    if (this.sprites.has(mobId)) {
      this.updatePosition(mobId, x, y);
      return;
    }
    const half = MOB_SIZE_PX / 2; // contracts:allow-literal — centering idiom
    const gfx = new Graphics()
      .rect(-half, -half, MOB_SIZE_PX, MOB_SIZE_PX)
      .fill({ color: MOB_COLOR });
    gfx.x = x;
    gfx.y = y;
    gfx.eventMode = "static";
    gfx.cursor = "crosshair";
    gfx.on("pointerdown", () => this.deps.onTargetClicked(mobId));
    this.deps.worldContainer.addChild(gfx);
    this.sprites.set(mobId, gfx);
  }

  updatePosition(mobId: string, x: number, y: number): void {
    const gfx = this.sprites.get(mobId);
    if (!gfx) return;
    gfx.x = x;
    gfx.y = y;
  }

  defeat(mobId: string): void {
    const gfx = this.sprites.get(mobId);
    if (!gfx) return;
    gfx.destroy();
    this.sprites.delete(mobId);
  }

  hasMob(mobId: string): boolean {
    return this.sprites.has(mobId);
  }

  // Phase 5 Step 16a — per-frame sync from combat-state.mobPositions.
  // Sprites without a position entry stay where they are (last-known
  // coordinates); mob spawn + defeat are still event-driven.
  sync(positions: ReadonlyMap<string, { x: number; y: number }>): void {
    for (const [mobId, pos] of positions) {
      this.updatePosition(mobId, pos.x, pos.y);
    }
  }
}
